/**
 * Candidates for the one job being viewed.
 *
 * WHY THIS PAGE NEEDED IT. The job post described what the job asks for and
 * said nothing about what it has produced -- the only mention of a candidate
 * anywhere on it was the words "View candidates" on a button. Meanwhile the
 * larger of its two hero cards, at 60% width, held two logos, one sentence and
 * a repeat of skills already listed further down the page.
 *
 * A recruiter opening a job wants to know how many candidates there are, how
 * many are waiting on them, and whether any are worth looking at. All three are
 * already in the API.
 *
 * COST. One request for one job -- not the dashboard's fan-out, which needs a
 * call per job because `GET /jobs` carries no counts. Here the jobId is known,
 * so this is a single call and it loads alongside the job itself.
 *
 * Deliberately not routed through jobsFSM: that machine is shared by the list,
 * the detail page and the candidate pages, and this is one page's read. The
 * dashboard set the precedent for a page-local hook.
 */

import { useEffect, useState } from 'react';
import axios from '../../../../axiosConfig';
import { CandidateRow } from '../../../dashboard/utils/aggregate';

/** How many candidates to pull. Enough to summarise; the list page paginates. */
const PAGE_SIZE = 100;

export interface JobCandidates {
  rows: CandidateRow[];
  /** What the API says the job has, which may exceed what we fetched. */
  total: number;
  /** True when `total` is larger than `rows` -- the summary is then partial. */
  partial: boolean;
  loading: boolean;
  /** Set when the request failed; the panel says so rather than showing zeros. */
  failed: boolean;
}

const EMPTY: JobCandidates = {
  rows: [],
  total: 0,
  partial: false,
  loading: true,
  failed: false,
};

export const useJobCandidates = (jobId?: string): JobCandidates => {
  const [state, setState] = useState<JobCandidates>(EMPTY);

  useEffect(() => {
    if (!jobId) {
      setState({ ...EMPTY, loading: false });
      return;
    }
    let cancelled = false;
    setState({ ...EMPTY, loading: true });

    axios
      .get(`/api/jobs/${jobId}/candidates?pageNumber=1&pageSize=${PAGE_SIZE}`)
      .then((res) => {
        if (cancelled) return;
        const rows =
          res.data?.content ?? res.data?.candidates ?? res.data ?? [];
        const list: CandidateRow[] = Array.isArray(rows) ? rows : [];
        const total = res.data?.totalElements ?? list.length;
        setState({
          rows: list,
          total,
          // Zero is not "no candidates" when the request succeeded and the API
          // reported more than we asked for -- say the summary is partial
          // rather than letting a truncated count read as the whole picture.
          partial: total > list.length,
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // A failed request must not render as zero candidates. Nothing about
        // this job's pipeline is known, and the panel says that.
        setState({ ...EMPTY, loading: false, failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return state;
};
